import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Workflow, Users, Bell, GitBranch, CheckCircle2 } from "lucide-react";
import aiveliqLogo from "@/assets/aiveliq360-logo.png";
import { SEOHead } from "@/components/seo/SEOHead";

const features = [
  { icon: Workflow, title: "Automated Task Routing", description: "AI assigns tasks to the right team members automatically." },
  { icon: Users, title: "SLA Management", description: "Track and enforce service level agreements across teams." },
  { icon: Bell, title: "Escalations & Notifications", description: "Smart alerts when deals need attention." },
  { icon: GitBranch, title: "Cross-Functional Alignment", description: "Unify marketing, sales, and customer success workflows." },
];

export default function GTMAutomation() {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead 
        title="GTM Workflow Automation | Aiveliq360 Sales & Marketing Alignment"
        description="Automate cross-functional workflows for marketing, sales, and customer success. Aiveliq360 streamlines the entire GTM engine through intelligent AI orchestration."
      />

      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/"><img src={aiveliqLogo} alt="Aiveliq360" className="h-10 object-contain" /></Link>
          <div className="flex items-center gap-3">
            <Link to="/pricing"><Button variant="ghost" size="sm">Pricing</Button></Link>
            <Link to="/auth"><Button size="sm" className="gap-1.5">Get Started<ArrowRight className="h-3.5 w-3.5" /></Button></Link>
          </div>
        </div>
      </header>

      <section className="relative py-16 lg:py-24">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-purple-500/5" />
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-3xl lg:text-5xl font-bold tracking-tight mb-6">
            GTM Workflow Automation:{" "}
            <span className="gradient-text">Align Sales, Marketing & Customer Success</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Break down silos between teams with AI-orchestrated workflows that 
            automate handoffs, track SLAs, and ensure nothing falls through the cracks.
          </p>
          <Link to="/auth">
            <Button size="lg" className="gap-2 ai-glow">
              Start Free Trial<ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      <section className="py-16 bg-muted/30">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((feature) => (
              <article key={feature.title} className="glass rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-primary/10"><feature.icon className="h-5 w-5 text-primary" /></div>
                  <h3 className="font-semibold">{feature.title}</h3>
                </div>
                <p className="text-muted-foreground text-sm">{feature.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-6">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground">← Back to Home</Link>
          <p>© 2025 Aiveliq360</p>
        </div>
      </footer>
    </div>
  );
}
